import { useState } from 'react'

import { Button, Icon, Icons } from '@clientbase/clientbase-library'
import { Box, Stack, useMediaQuery } from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import mixpanel from 'mixpanel-browser'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import { ParsedUrlQueryInput } from 'querystring'

import {
  SidebarAccountSwitcher,
  useShowAccountSwitcher
} from 'components/AccountSwitcher'
import InviteAndEarn from 'components/InviteAndEarn'

import { planTagMapper } from 'utils/planTagMapper'
import { settingsTabsParams } from 'utils/tabs'

import { theme } from 'styles/theme'

import Logo from '../Logo'
import { Menu } from './components/Menu/Menu'
import * as S from './Drawer.styles'
export type Item = {
  icon: Icons | React.ReactNode
  label: string
  router: string
  subheader?: SubheaderProps[]
  option?: string
  news?: boolean
  // SC-10999: tela que não funciona na visão multi-empresas
  groupUnavailable?: boolean
}
export interface SubheaderProps {
  label: string
  icon?: Icons
  query?: ParsedUrlQueryInput
  tab?: number | string
  subRouter?: string
  router?: string
  news?: boolean
  tag?: 'NOVO' | 'BETA'
  highlight?: boolean
  upgrade?: boolean
  coexAlert?: boolean
  // SC-10999: tela que não funciona na visão multi-empresas
  groupUnavailable?: boolean
}
export type DrawerProps = {
  showTopGlobalInfo: boolean | undefined
  menuItems: Item[]
  window?: () => Window
  drawerIsOpen: boolean
  setDrawerIsOpen: (drawerIsOpen: boolean) => void
  isExpanded: boolean
  handleToggleIsExpanded: () => void
  expandDrawer: () => void
}

export const Drawer = ({
  showTopGlobalInfo,
  menuItems,
  window,
  setDrawerIsOpen,
  drawerIsOpen,
  handleToggleIsExpanded,
  expandDrawer
}: DrawerProps) => {
  const { ['nextauth.current_plan']: current_plan } = parseCookies()

  const { push } = useRouter()
  const { user } = useAuthContext()
  // SC-10999: seletor de empresa no topo do menu para quem tem multi-contas
  const showAccountSwitcher = useShowAccountSwitcher()
  const openPulseChat = () => {
    if (typeof window === 'undefined') return
    const w = window as unknown as {
      $pulse?: { open?: () => void }
      pulse?: { open?: () => void }
    }
    if (w.$pulse?.open) {
      w.$pulse.open()
      return
    }
    if (w.pulse?.open) {
      w.pulse.open()
      return
    }
    const launcher = document.querySelector<HTMLElement>(
      '[id^="pulse-"], iframe[src*="pulse.is"], #pulse-livechat-launcher'
    )
    launcher?.click()
  }
  const currentPlanTag =
    (user?.current_plan && planTagMapper[user.current_plan]) ||
    planTagMapper['free']
  // Tags do mapper são curtas (PRO, TRIAL, SCALE…) e o layout foi desenhado pra
  // elas. Um rótulo longo estoura a largura do drawer, então rótulos longos ganham um
  // corpo menor — sem mexer no que já cabia — pra sobrar espaço pra logo.
  const longPlanTagSx =
    currentPlanTag.text.length > 6
      ? {
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.4px',
          lineHeight: 1.45,
          p: '3px 8px'
        }
      : {}
  const [openMenu, setOpenMenu] = useState({
    clients: false,
    receives: false
  })
  const container =
    window !== undefined ? () => window().document.body : undefined
  const match = useMediaQuery('(max-width: 700px)', {
    noSsr: window === undefined
  })
  const mixpanelTyped = mixpanel as unknown as { __loaded: string }
  const handleOpenMenu = (option: string) => {
    setOpenMenu((prevState) => ({
      ...prevState,
      [option]: !isExpanded ? true : !openMenu[option]
    }))
  }

  const isExpanded = localStorage.getItem('expanded-drawer') == 'true'
  const drawer = (id: string) => (
    <S.WrapperDrawer sx={{ ...(showTopGlobalInfo && { marginTop: '50px' }) }}>
      {/* Coluna flex: a lista ocupa o que sobra abaixo do cabeçalho (logo, plano
          e seletor de empresa) e rola dentro dele. A altura fixa de antes
          (100% - 80px) não contava o cabeçalho: com o seletor de empresa a lista
          passava do fim da tela e o Indique e ganhe saía cortado, sem scroll. */}
      <Box
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        minHeight={0}
      >
        {match && (
          <S.CloseButton onClick={() => setDrawerIsOpen(false)}>
            <Icon icon="close" />
          </S.CloseButton>
        )}
        {isExpanded ? (
          // O drawer aberto tem 240px e a logo sozinha come 123px, então uma tag
          // longa não cabe do lado. A tag fica fixa (`flexShrink: 0`)
          // e quem cede é a logo: o SVG ganhou `viewBox`, então escala junto em vez
          // de ser cortado. Com tag curta nada encolhe.
          <Stack
            pt="4px"
            pl="16px"
            direction="row"
            columnGap="8px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              sx={{
                display: 'flex',
                minWidth: 0,
                '& a': { display: 'flex', minWidth: 0 },
                '& svg': { maxWidth: '100%', height: 'auto' }
              }}
            >
              <Logo id={id} />
            </Box>

            <Box
              sx={{
                fontSize: '13px',
                p: '4px 12px',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                ...longPlanTagSx,
                bgcolor: currentPlanTag.bgcolor,
                color: currentPlanTag.fontColor,
                borderRadius: '6px',
                cursor: 'pointer'
              }}
              onClick={() =>
                push(`/settings?tab=${settingsTabsParams.MY_PLAN}`)
              }
            >
              {currentPlanTag.text}
            </Box>
          </Stack>
        ) : (
          <Stack justifyContent="space-between" alignItems="center" px="4px">
            <Logo id={id} size="only-logo" />
            <Box
              sx={{
                fontSize: '12px',
                p: '4px 10px',
                alignItems: 'center',
                maxWidth: '100%',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
                ...longPlanTagSx,
                bgcolor: currentPlanTag.bgcolor,
                color: currentPlanTag.fontColor,
                borderRadius: '6px',
                cursor: 'pointer'
              }}
              onClick={() =>
                push(`/settings?tab=${settingsTabsParams.MY_PLAN}`)
              }
            >
              {currentPlanTag.text}
            </Box>
          </Stack>
        )}
        {showAccountSwitcher && (
          <Box mt="20px" width="100%">
            <SidebarAccountSwitcher isExpanded={isExpanded} />
          </Box>
        )}
        <S.HandleToggleIsExpandedButton
          showTopGlobalInfo={showTopGlobalInfo}
          onClick={() => {
            handleToggleIsExpanded()
            mixpanelTyped.__loaded &&
              mixpanel.track('Clique icone minimizar/maximizar sidebar', {
                acao: isExpanded ? 'clicou em minimizar' : 'clicou em maximizar'
              })
          }}
        >
          <Icon
            icon={isExpanded ? 'arrowCircleLeft' : 'arrowCircleRight'}
            width="27px"
            height="27px"
          />
        </S.HandleToggleIsExpandedButton>
        <Box
          sx={{
            transition: 'gap 140ms ease-in-out',
            overflowY: 'scroll',
            '::-webkit-scrollbar': {
              width: '4px'
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: '12px'
            },
            '&:hover': {
              '::-webkit-scrollbar-thumb': {
                backgroundColor: '#DAD7D7'
              }
            }
          }}
          mt={showAccountSwitcher ? '24px' : '70px'}
          display="flex"
          flex="1 1 auto"
          minHeight={0}
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
            display="flex"
            flexWrap="wrap"
            gap={isExpanded ? 1 : 3}
          >
            {menuItems.map((item, index) => (
              <Menu
                icon={item?.icon}
                key={index}
                // label={match ? item?.label : isExpanded ? item?.label : ''}
                label={item?.label}
                handleClick={() => setDrawerIsOpen(false)}
                expandDrawer={expandDrawer}
                router={item.router}
                isExpanded={isExpanded}
                title={isExpanded ? item?.label : ''}
                subheader={item?.subheader}
                openMenu={openMenu}
                handleOpenMenu={handleOpenMenu}
                option={item?.option}
                news={item?.news}
                groupUnavailable={item?.groupUnavailable}
              />
            ))}

            {match && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease',
                  '&:hover': {
                    backgroundColor: theme.palette.neutral[800]
                  }
                }}
                onClick={() => {
                  openPulseChat()
                  setDrawerIsOpen(false)
                }}
              >
                <Icon
                  icon="faBell"
                  colorSX={theme.palette.neutral[400]}
                  width="20px"
                  height="20px"
                />
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.palette.neutral[300]
                  }}
                >
                  Suporte
                </span>
              </Box>
            )}
          </Box>

          <Box
            mb="10px"
            mt="32px"
            height={'100%'}
            width={'100%'}
            display={'flex'}
            alignItems={'end'}
            justifyContent="center"
          >
            {/* Mesma largura e respiro do seletor de empresa do topo (236px) */}
            <Stack gap="10px" width={isExpanded ? 236 : 'auto'} mx="auto">
              {isExpanded && ['free', 'plan_trial'].includes(current_plan) && (
                <Button
                  startIcon="faCircleArrowUp"
                  variantButton="outlined"
                  fullWidth
                  sx={{ color: theme.palette.info[200] }}
                  onClick={() =>
                    push(`/settings?tab=${settingsTabsParams.MY_PLAN}`)
                  }
                >
                  Upgrade
                </Button>
              )}
              <InviteAndEarn
                handleToggleIsExpanded={handleToggleIsExpanded}
                isExpanded={isExpanded}
              />
            </Stack>
          </Box>
        </Box>
      </Box>
    </S.WrapperDrawer>
  )
  return (
    <>
      <S.DrawerWrapper
        isExpanded={isExpanded}
        ModalProps={{
          keepMounted: true
        }}
        container={container}
        open={drawerIsOpen}
        sx={{
          display: { xsm: 'none', xs: 'block' },
          flexShrink: 0,
          width: '100%',
          overflowX: 'auto',
          alignItems: 'center'
        }}
        variant="temporary"
        width="100%"
      >
        {drawer('responsive-logo')}
      </S.DrawerWrapper>
      <S.DrawerWrapper
        isExpanded={isExpanded}
        open={!drawerIsOpen}
        sx={{
          display: { xsm: 'block', xs: 'none' },
          flexShrink: 0,
          width: isExpanded ? 260 : 100,
          overflowX: 'auto'
        }}
        variant="permanent"
        width={isExpanded ? 260 : 100}
      >
        {drawer('logo')}
      </S.DrawerWrapper>
    </>
  )
}
